import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, FileText, CheckSquare, Tag, X, AlertTriangle, ExternalLink, Clock, Database, Sparkles, Target, Lightbulb, MessageSquare, Zap, Heart, TrendingUp, Copy, Check, Loader2, MapPin, Mail, Send, ChevronDownIcon, Maximize2, Minimize2, Eye, Code } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, teamsApi, CampaignRecord, CampaignTargetRegionRow, CampaignTargetMailingListRow, CampaignTargetTagRow, formVersionsApi } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmContactsApi, CrmMailingList, CrmTag, CrmContact, BucketAssignProgress } from '../services/crmApi';
import { CodeEditor } from '../components/CodeEditor';
import { toast } from 'sonner';
import { errorLogService } from '../services/errorLog';
import { auditService } from '../services/auditService';
import { formatZohoDateTime, formatCreatorDateTime, callGPTAPI } from '../utils/api';
import { audienceStorage, applyAudiencesToContacts, getAudienceRuleSummary, SavedAudience } from '../services/audienceApi';
import { LayoutTemplateIcon } from 'lucide-react';
import { EmailBuilder } from '../components/builder/EmailBuilder';
import { EmailBlock, EmailGlobalStyles, DEFAULT_EMAIL_STYLES } from '../components/builder/types';
// ── Helper: extract display values from a contact object ──
// The Related Records API may return fields with different casing or structure.
// This helper tries multiple possible field names.
function getContactField(contact: any, ...fieldNames: string[]): string {
  for (const name of fieldNames) {
    // Try exact match
    if (contact[name] != null && contact[name] !== '') return String(contact[name]);
    // Try lowercase
    const lower = name.toLowerCase();
    for (const key of Object.keys(contact)) {
      if (key.toLowerCase() === lower && contact[key] != null && contact[key] !== '') {
        return String(contact[key]);
      }
    }
  }
  return '';
}
function getContactDisplayName(contact: any): string {
  const fullName = getContactField(contact, 'Full_Name');
  if (fullName) return fullName;
  const first = getContactField(contact, 'First_Name');
  const last = getContactField(contact, 'Last_Name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  // Fallback: try 'Name' or 'name'
  const name = getContactField(contact, 'Name', 'name');
  if (name) return name;
  return '';
}
function getContactEmail(contact: any): string {
  return getContactField(contact, 'Email', 'email', 'Email_Address');
}
// ── Helper: Parse basic markdown for chat messages ──
interface MarkdownActions {
  onUseSubject: (text: string) => void;
  onUsePreheader: (text: string) => void;
  onCopy: (text: string) => void;
}
function renderMarkdown(text: string, actions: MarkdownActions) {
  var lines = text.split('\n');
  var elements: React.ReactNode[] = [];
  var currentList: {
    text: string;
    raw: string;
    key: string;
  }[] = [];
  var flushList = function () {
    if (currentList.length > 0) {
      elements.push(<ul key={'list-' + elements.length} className="my-2 space-y-1.5" data-id="element-2773" data-mp="syyx60qdcwpi">
          {currentList.map(function (item) {
          return <li key={item.key} className="flex items-start gap-2 group text-gray-700 py-1 px-2 rounded hover:bg-white/40 transition-colors" data-id="element-2774" data-mp="1loxsuez2js35">
                <span className="text-purple-400 mt-0.5 flex-shrink-0" data-id="element-2775" data-mp="1coj6dcadc6y2">•</span>
                <span className="flex-1" data-id="element-2776" data-mp="17qhmip54c3lj">
                  {parseInlineFormatting(item.text)}
                </span>
                <button onClick={function () {
              actions.onCopy(item.raw);
            }} className="text-xs px-2 py-0.5 text-gray-400 hover:text-purple-700 hover:bg-purple-50 rounded opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap" title="Copy to clipboard" data-id="element-2777" data-mp="1afdk9fiooul">
                  Copy
                </button>
              </li>;
        })}
        </ul>);
      currentList = [];
    }
  };
  for (var i = 0; i < lines.length; i++) {
    var line = lines[i];
    // Empty line
    if (!line.trim()) {
      flushList();
      elements.push(<div key={'empty-' + i} className="h-2" data-id="element-2778" data-mp="22w5krzpkvray"></div>);
      continue;
    }
    // Horizontal rule: "---" or "***"
    if (/^[\-\*]{3,}$/.test(line.trim())) {
      flushList();
      elements.push(<hr key={'hr-' + i} className="my-3 border-gray-200/60" data-id="element-2779" data-mp="1txa8c5vxxkz7" />);
      continue;
    }
    // Numbered items: "1. Text here"
    var numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      flushList();
      var itemText = numberedMatch[2].replace(/\*\*/g, ''); // strip bold
      elements.push(<div key={'numbered-' + i} className="flex items-start gap-2 py-2 my-1 bg-white/50 rounded-lg px-3 border border-purple-100/50 group hover:border-purple-200 transition-colors" data-id="element-2780" data-mp="nf53wqmf3kw5">
          <span className="font-semibold text-purple-500 min-w-[1.5rem] mt-px" data-id="element-2781" data-mp="oyetv7ihngol">
            {numberedMatch[1]}.
          </span>
          <span className="flex-1 text-gray-800" data-id="element-2782" data-mp="k0lnoeh6qmob">
            {parseInlineFormatting(numberedMatch[2])}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0" data-id="element-2783" data-mp="3sfwxt3v543jq">
            <button onClick={function () {
            actions.onUseSubject(itemText);
          }} className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 whitespace-nowrap font-medium shadow-sm transition-colors" title="Use as email subject line" data-id="element-2784" data-mp="3nh28dj2o3gqn">
              Subject
            </button>
            <button onClick={function () {
            actions.onUsePreheader(itemText);
          }} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 whitespace-nowrap font-medium shadow-sm transition-colors" title="Use as preheader text" data-id="element-2785" data-mp="34qb24qdz86gh">
              Preheader
            </button>
            <button onClick={function () {
            actions.onCopy(itemText);
          }} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 whitespace-nowrap font-medium shadow-sm transition-colors" title="Copy to clipboard" data-id="element-2786" data-mp="1me1nuz8eetfu">
              Copy
            </button>
          </span>
        </div>);
      continue;
    }
    // Headers: "## Header" or "### Header"
    var headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      var level = headerMatch[1].length;
      var headerClass = level === 1 ? 'text-lg font-bold mt-4 mb-2 text-gray-900' : level === 2 ? 'text-base font-bold mt-3 mb-1.5 text-gray-800' : 'text-sm font-bold mt-2 mb-1 text-gray-800';
      elements.push(<div key={'header-' + i} className={headerClass} data-id="element-2787" data-mp="2k83xu1ne7zks">
          {parseInlineFormatting(headerMatch[2])}
        </div>);
      continue;
    }
    // Bullet lists: "- Item" or "* Item"
    var listMatch = line.match(/^[\-\*]\s+(.+)$/);
    if (listMatch) {
      var rawText = listMatch[1].replace(/\*\*/g, '');
      currentList.push({
        text: listMatch[1],
        raw: rawText,
        key: 'li-' + i
      });
      continue;
    }
    // Regular text
    flushList();
    elements.push(<div key={'text-' + i} className="my-1 leading-relaxed text-gray-700" data-id="element-2788" data-mp="1wi9p9wa3wx6c">
        {parseInlineFormatting(line)}
      </div>);
  }
  flushList();
  return elements;
}
function parseInlineFormatting(text: string) {
  // Handle **bold**
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-semibold text-gray-900" data-id="element-2789" data-mp="9yb7rwvnctht">
          {part.slice(2, -2)}
        </strong>;
    }
    return part;
  });
}
// ── Audience Viewer Modal ──
function AudienceViewerModal({
  contacts,
  onClose
}: {
  contacts: CrmContact[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(100);
  const [showDebug, setShowDebug] = useState(false);
  // Log contact structure on mount for debugging
  useEffect(() => {
    if (contacts.length > 0) {
      console.log('AudienceViewer — contact count:', contacts.length);
      console.log('AudienceViewer — sample contact keys:', Object.keys(contacts[0]));
      console.log('AudienceViewer — sample contact:', JSON.stringify(contacts[0], null, 2));
    }
  }, [contacts]);
  const filtered = search.trim() ? contacts.filter(c => {
    const q = search.toLowerCase();
    const name = getContactDisplayName(c).toLowerCase();
    const email = getContactEmail(c).toLowerCase();
    const region = getContactField(c, 'Sub_Region', 'Region').toLowerCase();
    const tags = getContactField(c, 'Tags').toLowerCase();
    return name.includes(q) || email.includes(q) || region.includes(q) || tags.includes(q);
  }) : contacts;
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-2790" data-mp="24hbh6utc6lq9">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-2791" data-mp="2ihm8u02h04ch" />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl flex flex-col" data-id="element-2792" data-mp="2efr74ugmnoy6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-2793" data-mp="o2jmgb8px2ds">
          <div data-id="element-2794" data-mp="k281sygcv22b">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-2795" data-mp="k2rlokpkg0iz">
              Audience Contacts
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-2796" data-mp="12gx9a54uu2dz">
              {contacts.length} unique contacts (deduplicated by email)
              {search.trim() && filtered.length !== contacts.length && <span data-id="element-2797" data-mp="tf7gauvbqve3"> · {filtered.length} matching filter</span>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-2798" data-mp="ohfe980ytsw6">
            <button onClick={() => setShowDebug(!showDebug)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded" data-id="element-2799" data-mp="1m3mjmk53fp9l">
              {showDebug ? 'Hide' : 'Show'} Raw Data
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-2800" data-mp="17egksxa64128">
              <X className="h-5 w-5" data-id="element-2801" data-mp="24vfao90lr0eo" />
            </button>
          </div>
        </div>

        {/* Debug: show raw structure of first contact */}
        {showDebug && contacts.length > 0 && <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 overflow-auto max-h-48" data-id="element-2802" data-mp="27ftsobx2s280">
            <p className="text-xs font-medium text-gray-500 mb-1" data-id="element-2803" data-mp="2cdvntv7rzrzu">
              Raw fields on first contact (keys:{' '}
              {Object.keys(contacts[0]).join(', ')}):
            </p>
            <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap" data-id="element-2804" data-mp="2mcb576h03mw5">
              {JSON.stringify(contacts[0], null, 2)}
            </pre>
          </div>}

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100" data-id="element-2805" data-mp="2vawtzjd2fy5d">
          <input type="text" value={search} onChange={e => {
          setSearch(e.target.value);
          setVisibleCount(100);
        }} placeholder="Search by name, email, region, or tag..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2806" data-mp="3037rkfkc37ur" />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" data-id="element-2807" data-mp="1ez0o9azq86xa">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2808" data-mp="1evl5ny6ugfr4">
            <thead className="bg-gray-50 sticky top-0" data-id="element-2809" data-mp="1a0fplg04hvay">
              <tr data-id="element-2810" data-mp="1nqswdf1a33yp">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2811" data-mp="1nrazmvmcyig4">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2812" data-mp="1ixpy4tlk36c6">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2813" data-mp="1soknw7y0go2d">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2814" data-mp="22ntdkolufgix">
                  Region
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2815" data-mp="cbe06dgw8g4n">
                  Tags
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2816" data-mp="3ubhx1rs6zyzv">
              {visible.map((contact, index) => {
              const name = getContactDisplayName(contact);
              const email = getContactEmail(contact);
              const region = getContactField(contact, 'Sub_Region', 'Region');
              const tags = getContactField(contact, 'Tags');
              return <tr key={contact.id || index} data-id="element-2817" data-mp="3alfp9cy25yg8" data-mpi={index}>
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-400" data-id="element-2818" data-mp="3flttejwjgo8z">
                      {index + 1}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-2819" data-mp="31nvk3sx2ri0c">
                      {name || <span className="text-gray-400" data-id="element-2820" data-mp="36lxcj4ge4toh">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2821" data-mp="zw91ofr90vfh">
                      {email || <span className="text-gray-400" data-id="element-2822" data-mp="14spon6mpp3qz">—</span>}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2823" data-mp="30xf6q0t0mitl">
                      {region || <span className="text-gray-400" data-id="element-2824" data-mp="2vxsspp5d4yjq">—</span>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 max-w-[200px] truncate" data-id="element-2825" data-mp="2qyg7t9y1y1js">
                      {tags || <span className="text-gray-400" data-id="element-2826" data-mp="2my5t98w722tb">—</span>}
                    </td>
                  </tr>;
            })}
              {visible.length === 0 && <tr data-id="element-2827" data-mp="2d7undgsy9jjs">
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2828" data-mp="2d7b9cb6f7fb1">
                    {search.trim() ? 'No contacts match your search' : 'No contacts in audience'}
                  </td>
                </tr>}
            </tbody>
          </table>

          {hasMore && <div className="px-6 py-4 text-center border-t border-gray-100" data-id="element-2829" data-mp="1ua1mek1vzjp1">
              <button onClick={() => setVisibleCount(prev => prev + 100)} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-2830" data-mp="9rhbaybsdcf0">
                Show more ({filtered.length - visibleCount} remaining)
              </button>
            </div>}
        </div>
      </div>
    </div>;
}
const steps = [{
  id: 1,
  name: 'Audience',
  icon: Users
}, {
  id: 2,
  name: 'Content',
  icon: FileText
}, {
  id: 3,
  name: 'Review',
  icon: CheckSquare
}, {
  id: 4,
  name: 'Export',
  icon: Database
}];
// Keep the existing strategy generator function (it's pure logic)
function generateMarketingStrategy(audienceDescription: string): MarketingStrategy {
  const desc = audienceDescription.toLowerCase();
  // ... (keeping the same logic as before, just abbreviated for brevity in this response)
  // In the actual file write, I will include the full logic
  const isPastors = desc.includes('pastor') || desc.includes('church');
  const isYouth = desc.includes('youth') || desc.includes('teen');
  // Default strategy if no specific match
  return {
    overview: "Focus on the universal appeal of authentic storytelling and the unique nature of The Chosen as a free, high-quality production that's changing how people experience biblical narratives.",
    subjectLines: ['See why 770+ million have watched The Chosen', "The show that's changing everything - watch free", 'Your next favorite show is waiting', 'Join the movement millions are talking about', 'Something special is happening - be part of it'],
    keyMessages: ['Lead with the unique value proposition (free, high-quality, authentic)', 'Use social proof (viewership numbers, testimonials)', 'Highlight the human stories and relatable characters', 'Emphasize accessibility across all platforms', 'Share the grassroots, crowdfunded origin story'],
    callToActions: ['Watch Free Now', 'Start Episode 1', 'Download the App', 'Join the Community', 'Share with Friends'],
    emotionalTriggers: ['Curiosity about what millions are watching', 'Desire for meaningful, quality content', 'Connection to timeless stories', 'Being part of something bigger'],
    timingTips: ['Test different days - typically Tuesday-Thursday perform well', 'Evening sends (6-8pm local time) for entertainment content', 'Avoid major holidays unless content is holiday-themed', 'Align with new episode or season releases'],
    personalizationTips: ["Use recipient's name in subject line when possible", 'Segment by engagement level (new vs. returning)', 'Reference their location for regional relevance', 'Customize based on how they joined your list']
  };
}
// ── Session Storage helpers for CampaignBuilder draft persistence ──
var CAMPAIGN_DRAFT_KEY = 'campaign_builder_draft';
function loadCampaignDraft(): any {
  try {
    var raw = sessionStorage.getItem(CAMPAIGN_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
function saveCampaignDraft(data: any) {
  try {
    sessionStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(data));
  } catch (e) {
    // sessionStorage full or unavailable — silently ignore
  }
}
function clearCampaignDraft() {
  try {
    sessionStorage.removeItem(CAMPAIGN_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
}
export function CampaignBuilder() {
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const navigate = useNavigate();
  // ── Restore draft from sessionStorage on mount ──
  var draft = loadCampaignDraft();
  // Only use draft if it's for the same workspace
  if (draft && draft._workspace !== currentWorkspace.name) {
    draft = null;
  }
  const [currentStep, setCurrentStep] = useState(draft?.currentStep || 1);
  // Data State
  const [loadingData, setLoadingData] = useState(true);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  // Campaign State
  const [campaign, setCampaign] = useState(draft?.campaign || {
    name: '',
    subject: '',
    preheader: '',
    fromName: `The Chosen ${currentWorkspace.name}`,
    fromEmail: `noreply@${currentWorkspace.id}.thechosen.tv`,
    targetMailingLists: [] as string[],
    targetTags: [] as string[],
    targetRegions: [] as string[],
    htmlContent: '',
    textContent: ''
  });
  // Audience Building State
  const [isSearchingAudience, setIsSearchingAudience] = useState(false);
  const [searchProgress, setSearchProgress] = useState<string>('');
  const [audienceResult, setAudienceResult] = useState<{
    count: number;
    contactIds: string[];
    contacts: CrmContact[];
  } | null>(draft?.audienceResultSummary ? {
    count: draft.audienceResultSummary.count,
    contactIds: draft.audienceResultSummary.contactIds || [],
    contacts: [] // Don't persist full contacts — too large
  } : null);
  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  const [exportProgress, setExportProgress] = useState<BucketAssignProgress | null>(null);
  const [bucketConfig, setBucketConfig] = useState({
    expiryHours: null as number | null,
    bucketId: ''
  });
  // Strategy State
  const [targetAudienceDescription, setTargetAudienceDescription] = useState('');
  const [generatedStrategy, setGeneratedStrategy] = useState<MarketingStrategy | null>(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [contentTab, setContentTab] = useState<'builder' | 'html' | 'text'>(draft?.contentTab || 'html');
  const [emailBuilderBlocks, setEmailBuilderBlocks] = useState<EmailBlock[]>(draft?.emailBuilderBlocks || []);
  const [emailBuilderStyles, setEmailBuilderStyles] = useState<EmailGlobalStyles>(draft?.emailBuilderStyles || DEFAULT_EMAIL_STYLES);
  const [chatMessages, setChatMessages] = useState<{
    role: 'user' | 'assistant';
    content: string;
  }[]>(draft?.chatMessages || []);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showMergeTags, setShowMergeTags] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  // Audience Viewer State
  const [showAudienceViewer, setShowAudienceViewer] = useState(false);
  // AI Chat Fullscreen State
  const [isChatFullscreen, setIsChatFullscreen] = useState(false);
  // Preview & Code View State
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilderCode, setShowBuilderCode] = useState(false);
  // Saved Audiences State
  const [savedAudiences, setSavedAudiences] = useState<SavedAudience[]>([]);
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>(draft?.selectedAudienceIds || []);
  // ── Auto-save draft to sessionStorage on state changes ──
  useEffect(() => {
    // Debounce saves to avoid thrashing sessionStorage
    var timer = setTimeout(function () {
      saveCampaignDraft({
        _workspace: currentWorkspace.name,
        _savedAt: new Date().toISOString(),
        currentStep: currentStep,
        campaign: campaign,
        chatMessages: chatMessages,
        selectedAudienceIds: selectedAudienceIds,
        contentTab: contentTab,
        emailBuilderBlocks: emailBuilderBlocks,
        emailBuilderStyles: emailBuilderStyles,
        audienceResultSummary: audienceResult ? {
          count: audienceResult.count,
          contactIds: audienceResult.contactIds
        } : null
      });
    }, 500);
    return function () {
      clearTimeout(timer);
    };
  }, [currentStep, campaign, chatMessages, selectedAudienceIds, contentTab, emailBuilderBlocks, emailBuilderStyles, audienceResult, currentWorkspace.name]);
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const [teamData, listsData, tagsData] = await Promise.all([teamsApi.findByTeamName(currentWorkspace.name), crmMailingListsApi.list(), crmTagsApi.list()]);
        if (teamData?.Team_Regions) {
          setAvailableRegions(teamData.Team_Regions.map(r => r.Region_Name));
        }
        // Filter mailing lists by team
        const teamFilteredLists = listsData.filter(l => !l.Team || l.Team === currentWorkspace.name);
        setMailingLists(teamFilteredLists);
        setTags(tagsData);
        // Load saved audiences for this workspace
        const audiences = audienceStorage.list(currentWorkspace.name);
        setSavedAudiences(audiences);
      } catch (err) {
        console.error('Failed to load campaign data:', err);
        toast.error('Failed to load audience data');
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [currentWorkspace.name]);
  // Generate bucket ID when reaching export step
  useEffect(() => {
    if (currentStep === 4 && !bucketConfig.bucketId) {
      const bucketId = campaignsApi.generateBucketId(currentWorkspace.id);
      setBucketConfig(prev => ({
        ...prev,
        bucketId
      }));
    }
  }, [currentStep, currentWorkspace.id]);
  // Handlers
  const handleRegionToggle = (region: string) => {
    setCampaign(prev => ({
      ...prev,
      targetRegions: prev.targetRegions.includes(region) ? prev.targetRegions.filter(r => r !== region) : [...prev.targetRegions, region]
    }));
    setAudienceResult(null); // Reset search results on change
  };
  const handleListToggle = (listId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetMailingLists: prev.targetMailingLists.includes(listId) ? prev.targetMailingLists.filter(id => id !== listId) : [...prev.targetMailingLists, listId]
    }));
    setAudienceResult(null);
  };
  const handleTagToggle = (tagId: string) => {
    setCampaign(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tagId) ? prev.targetTags.filter(id => id !== tagId) : [...prev.targetTags, tagId]
    }));
    setAudienceResult(null);
  };
  const handleAudienceToggle = (audienceId: string) => {
    setSelectedAudienceIds(prev => prev.includes(audienceId) ? prev.filter(id => id !== audienceId) : [...prev, audienceId]);
    setAudienceResult(null);
  };
  const handleBuildAudience = async () => {
    const hasCrmCriteria = campaign.targetRegions.length > 0 || campaign.targetMailingLists.length > 0 || campaign.targetTags.length > 0;
    const hasAudienceCriteria = selectedAudienceIds.length > 0;
    if (!hasCrmCriteria && !hasAudienceCriteria) {
      toast.error('Please select at least one criteria or saved audience');
      return;
    }
    setIsSearchingAudience(true);
    setSearchProgress('Starting audience search...');
    try {
      // We always need the full contact list for audience rule filtering
      // and for CRM criteria. searchForAudience already fetches all contacts.
      const contactMap = new Map<string, CrmContact>();
      // ── Part 1: CRM-based criteria (regions, mailing lists, tags) ──
      if (hasCrmCriteria) {
        const mailingListNames = campaign.targetMailingLists.map(id => mailingLists.find(l => l.id === id)?.Name).filter(Boolean) as string[];
        const tagNames = campaign.targetTags.map(id => tags.find(t => t.id === id)?.Name).filter(Boolean) as string[];
        console.log('Building audience with CRM criteria:', {
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListNames,
          tagNames
        });
        const crmContacts = await crmContactsApi.searchForAudience({
          team: currentWorkspace.name,
          regions: campaign.targetRegions,
          mailingListIds: campaign.targetMailingLists,
          mailingListNames,
          tagIds: campaign.targetTags,
          tagNames
        }, progressMsg => setSearchProgress(progressMsg));
        for (const c of crmContacts) {
          if (c.id) contactMap.set(c.id, c);
        }
        setSearchProgress(`CRM criteria: ${crmContacts.length} contacts found`);
      }
      // ── Part 2: Saved audience rules ──
      if (hasAudienceCriteria) {
        const selectedAudiences = savedAudiences.filter(a => selectedAudienceIds.includes(a.id));
        setSearchProgress('Applying saved audience filters...');
        // We need the full contact list for audience rule filtering.
        // If we already fetched via CRM criteria, searchForAudience loaded them.
        // If not, we need to fetch them now.
        let allContacts: CrmContact[];
        if (hasCrmCriteria) {
          // searchForAudience already loaded all contacts internally,
          // but we don't have access to them. Fetch again for audience filtering.
          setSearchProgress('Loading contacts for audience filtering (200 per batch)...');
          allContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
            setSearchProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched, ${matching.toLocaleString()} in scope...`);
          }, {
            team: currentWorkspace.name
          });
        } else {
          setSearchProgress('Loading all contacts (200 per batch)...');
          allContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
            setSearchProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched, ${matching.toLocaleString()} in scope...`);
          }, {
            team: currentWorkspace.name
          });
        }
        setSearchProgress(`Filtering ${allContacts.length} contacts through ${selectedAudiences.length} audience(s)...`);
        const audienceMatches = applyAudiencesToContacts(allContacts, selectedAudiences);
        console.log(`Saved audience filtering: ${audienceMatches.length} contacts match ${selectedAudiences.length} audience(s)`);
        // Merge with OR logic — add audience matches to the contact map
        for (const c of audienceMatches) {
          if (c.id && !contactMap.has(c.id)) {
            contactMap.set(c.id, c);
          }
        }
        setSearchProgress(`Audience filters added ${audienceMatches.length} contacts`);
      }
      const allResults = Array.from(contactMap.values());
      setAudienceResult({
        count: allResults.length,
        contactIds: allResults.map(c => c.id),
        contacts: allResults
      });
      if (allResults.length === 0) {
        toast.warning('No contacts found matching these criteria');
      } else {
        toast.success(`Found ${allResults.length} contacts`);
      }
    } catch (err) {
      console.error('Failed to build audience:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      toast.error(`Audience search failed: ${errorMessage}`, {
        duration: 10000
      });
      if (debugInfo) {
        console.error('Debug info:', JSON.stringify(debugInfo, null, 2));
      }
    } finally {
      setIsSearchingAudience(false);
      setSearchProgress('');
    }
  };
  const handleGenerateStrategy = () => {
    if (!targetAudienceDescription.trim()) return;
    setIsGeneratingStrategy(true);
    setTimeout(() => {
      const strategy = generateMarketingStrategy(targetAudienceDescription);
      setGeneratedStrategy(strategy);
      setIsGeneratingStrategy(false);
    }, 800);
  };
  const handleUseSubjectLine = (subjectLine: string) => {
    setCampaign(prev => ({
      ...prev,
      subject: subjectLine
    }));
  };
  const handleCopyToClipboard = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleExport = async () => {
    if (!audienceResult || audienceResult.contactIds.length === 0) {
      toast.error('No audience selected');
      return;
    }
    setIsExporting(true);
    setExportProgress(null);
    try {
      // 1. Assign bucket in CRM with progress tracking
      const expiresAt = bucketConfig.expiryHours ? formatZohoDateTime(new Date(Date.now() + bucketConfig.expiryHours * 60 * 60 * 1000)) : undefined;
      const crmResult = await crmContactsApi.assignBucket(audienceResult.contactIds, bucketConfig.bucketId, expiresAt, progress => setExportProgress(progress));
      if (crmResult.failedCount > 0 && crmResult.updatedCount === 0) {
        // All failed — show detailed error in toast and on the failure screen
        const detailedError = crmResult.errors.length > 0 ? crmResult.errors[0] : 'Unknown error';
        // Log the full failure summary to Error Log
        errorLogService.log('Campaign Export — All Updates Failed', detailedError, {
          bucketId: bucketConfig.bucketId,
          contactCount: audienceResult.contactIds.length,
          failedCount: crmResult.failedCount,
          allErrors: crmResult.errors,
          contactIds: audienceResult.contactIds
        });
        throw new Error(`All ${crmResult.failedCount} updates failed. ${detailedError}`);
      }
      if (crmResult.failedCount > 0) {
        // Partial failure — log it
        errorLogService.log('Campaign Export — Partial Failure', `${crmResult.failedCount} of ${audienceResult.contactIds.length} contacts failed`, {
          bucketId: bucketConfig.bucketId,
          updatedCount: crmResult.updatedCount,
          failedCount: crmResult.failedCount,
          errors: crmResult.errors
        });
      }
      // 2. Create campaign record in Creator
      const regionRows: CampaignTargetRegionRow[] = campaign.targetRegions.map(r => ({
        Region_Name: r
      }));
      const listRows: CampaignTargetMailingListRow[] = campaign.targetMailingLists.map(id => {
        const list = mailingLists.find(l => l.id === id);
        return {
          ID: id,
          Mailing_List_ID: id,
          Mailing_List_Name: list?.Name || id
        };
      });
      const tagRows: CampaignTargetTagRow[] = campaign.targetTags.map(id => {
        const tag = tags.find(t => t.id === id);
        return {
          ID: id,
          Tag_ID: id,
          Tag_Name: tag?.Name || id
        };
      });
      // Build campaign payload — only include datetime fields with valid values
      // Zoho Creator expects datetime as "dd-MMM-yyyy HH:mm:ss" (e.g., "12-Feb-2026 13:00:00")
      // Field names must match Creator form API names (from screenshot):
      //   Email Subject → Email_Subject, Preheader Text → Preheader_Text
      const campaignPayload: Record<string, any> = {
        Campaign_Name: campaign.name,
        Campaign_ID: campaignsApi.generateCampaignId(),
        Email_Subject: campaign.subject,
        Preheader_Text: campaign.preheader,
        From_Name: campaign.fromName,
        From_Email: campaign.fromEmail,
        HTML_Content: campaign.htmlContent,
        Text_Content: campaign.textContent,
        Status: 'Exported',
        Bucket_ID: bucketConfig.bucketId,
        Recipient_Count: crmResult.updatedCount,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Campaign_Target_Regions: regionRows,
        Campaign_Target_Mailing_Lists: listRows,
        Campaign_Target_Tags: tagRows
      };
      // Only include Bucket_Expires_At if there's an actual expiry date
      // Don't send empty string — Creator rejects it as invalid datetime
      if (expiresAt) {
        campaignPayload.Bucket_Expires_At = formatCreatorDateTime(new Date(Date.now() + bucketConfig.expiryHours! * 60 * 60 * 1000));
      }
      // NOTE: Exported_At exists in Creator form but we intentionally skip it here
      console.log('Creating campaign in Creator with payload:', JSON.stringify(campaignPayload, null, 2));
      const result = await campaignsApi.create(campaignPayload);
      // Fire-and-forget version history and audit log
      if (result?.data?.ID) {
        const savedCampaignId = String(result.data.ID);
        (async () => {
          try {
            await formVersionsApi.create({
              Form_ID: savedCampaignId,
              Form_Name: campaign.name,
              Version_Number: 1,
              HTML: campaign.htmlContent,
              CSS: '',
              JavaScript1: '',
              Builder_Schema: JSON.stringify({
                Email_Subject: campaign.subject,
                Preheader_Text: campaign.preheader,
                From_Name: campaign.fromName,
                From_Email: campaign.fromEmail,
                HTML_Content: campaign.htmlContent,
                Text_Content: campaign.textContent,
                Status: 'Exported'
              }),
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Initial version — Status: Exported',
              Team: currentWorkspace.name,
              Region: currentSubRegion || '',
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create campaign version:', err);
          }
          auditService.log({
            actionType: 'created',
            entityType: 'campaign',
            entityId: savedCampaignId,
            entityName: campaign.name,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      }
      setExportResult({
        success: true,
        updatedCount: crmResult.updatedCount,
        failedCount: crmResult.failedCount,
        errors: crmResult.errors
      });
      toast.success(`Campaign exported — ${crmResult.updatedCount} contacts updated`);
      // Clear draft from sessionStorage after successful export
      clearCampaignDraft();
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const debugInfo = (err as any)?.debug;
      // Log to Error Log module
      errorLogService.log('Campaign Export Failed', errorMessage, {
        bucketId: bucketConfig.bucketId,
        contactCount: audienceResult?.contactIds?.length,
        debug: debugInfo
      });
      setExportResult({
        success: false,
        error: errorMessage
      });
      // Show detailed error in toast (long duration so user can read it)
      toast.error(`Export failed: ${errorMessage}`, {
        duration: 15000
      });
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };
  const handleSendChatMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, {
      role: 'user',
      content: userMessage
    }]);
    setIsChatLoading(true);
    try {
      const context = {
        campaignName: campaign.name,
        subject: campaign.subject,
        audienceSize: audienceResult?.count || 0,
        targetRegions: campaign.targetRegions,
        targetLists: campaign.targetMailingLists.length,
        targetTags: campaign.targetTags.length
      };
      // Build conversation history into the instructions so the prompt can be a plain string
      var conversationContext = '';
      if (chatMessages.length > 0) {
        conversationContext = '\n\nConversation so far:\n' + chatMessages.map(function (m) {
          return (m.role === 'user' ? 'User' : 'Assistant') + ': ' + m.content;
        }).join('\n');
      }
      const systemInstructions = `You are a marketing strategist for email campaigns. Help the user craft compelling subject lines, email copy, and campaign strategies. When suggesting subject lines, format them as a numbered list. Keep responses concise and actionable.

Current campaign context:
- Campaign: ${context.campaignName || 'Untitled'}
- Current subject: ${context.subject || 'Not set'}
- Audience size: ${context.audienceSize} contacts
- Targeting: ${context.targetRegions.length} regions, ${context.targetLists} lists, ${context.targetTags} tags` + conversationContext;
      const response = await callGPTAPI(systemInstructions, userMessage as any);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: response
      }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Chat failed', {
        description: msg
      });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${msg}`
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };
  const handleUseSubjectFromChat = (subject: string) => {
    setCampaign(prev => ({
      ...prev,
      subject
    }));
    toast.success('Subject line applied');
  };
  const handleCopyHtml = async () => {
    try {
      await navigator.clipboard.writeText(campaign.htmlContent);
      setCopiedHtml(true);
      toast.success('HTML copied to clipboard');
      setTimeout(() => setCopiedHtml(false), 2000);
    } catch {
      toast.error('Failed to copy HTML');
    }
  };
  if (loadingData) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-2831" data-mp="ocxvgkiit8d3">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2832" data-mp="je4f8kbun398" />
      </div>;
  }
  return <div className="space-y-8 max-w-4xl mx-auto pb-20" data-id="element-2833" data-mp="xddwlnbsk3es">
      <div className="text-center" data-id="element-2834" data-mp="xbszbpy3e6d8">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2835" data-mp="tdd5b23lfxqb">
          Create Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2836" data-mp="10gjovu6hok09">
          Design and export audience to Zoho Campaigns.
        </p>
      </div>

      {/* Campaign Name — always visible above steps */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-2837" data-mp="1adelu8n46ccf">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2838" data-mp="1f4oap70u9bn9">
          Campaign Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
        ...campaign,
        name: e.target.value
      })} placeholder="e.g., Brazil February Newsletter" data-id="element-2839" data-mp="1j6i4cvcm9fdx" />
        <p className="mt-1 text-xs text-gray-400" data-id="element-2840" data-mp="3uwc44sgn8m1b">
          Internal name to identify this campaign in Creator
        </p>
      </div>

      {/* Progress Bar */}
      <div className="relative" data-id="element-2841" data-mp="3rluid2nio3m">
        <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-2842" data-mp="2h0ateolfb4k1">
          <div className="w-full border-t border-gray-200" data-id="element-2843" data-mp="2c92kdto09l7e" />
        </div>
        <div className="relative flex justify-between" data-id="element-2844" data-mp="2c7rbxgjm7p9y">
          {steps.map(step => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          return <div key={step.id} className="flex flex-col items-center relative bg-white px-2" data-id="element-2845" data-mp="2zyn2698facf3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${isComplete ? 'border-blue-600 bg-blue-600 text-white' : isCurrent ? 'border-blue-600 text-blue-600' : 'border-gray-300 text-gray-500'}`} data-id="element-2846" data-mp="2vy37rdf8hmwn">
                  <step.icon className="h-5 w-5" data-id="element-2847" data-mp="1fjbdeuyj25kp" />
                </div>
                <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-2848" data-mp="1fgfg98ui7kxb">
                  {step.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Step 1: Audience */}
      {currentStep === 1 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-2849" data-mp="2pg8e5f7zn94o">
          <div data-id="element-2850" data-mp="2ue9ye3h5hehq">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2851" data-mp="2ue04i4szin6r">
              Build Your Audience
            </h2>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2852" data-mp="3i5p1vzkny1uk">
              Select criteria to filter contacts. Contacts matching{' '}
              <strong data-id="element-2853" data-mp="3m3v54xmalzaq">ANY</strong> of the selected criteria will be included (OR
              logic).
            </p>

            {/* Regions */}
            {availableRegions.length > 0 && <div className="mb-8" data-id="element-2854" data-mp="3d7dtzudxi1ez">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2855" data-mp="1wqi179i3eda5">
                  <MapPin className="h-4 w-4 text-gray-400" data-id="element-2856" data-mp="21qe4t7mvo2p6" />
                  Sub-Regions
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-2857" data-mp="1u5k2ft7pgt4d">
                  {availableRegions.map(region => <label key={region} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetRegions.includes(region) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2858" data-mp="1p78sliheuim5">
                      <input type="checkbox" checked={campaign.targetRegions.includes(region)} onChange={() => handleRegionToggle(region)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-2859" data-mp="1agj97stspx77" />
                      <span className="text-sm text-gray-900" data-id="element-2860" data-mp="rs4lpfiot1b5">{region}</span>
                    </label>)}
                </div>
              </div>}

            {/* Mailing Lists */}
            <div className="mb-8" data-id="element-2861" data-mp="2mvkkxihpzqxm">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2862" data-mp="2muht98ckpkgn">
                <Mail className="h-4 w-4 text-gray-400" data-id="element-2863" data-mp="2rst1xfr4toxa" />
                Mailing Lists
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2864" data-mp="2wlvy7vnanch6">
                {mailingLists.map(list => <label key={list.id} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${campaign.targetMailingLists.includes(list.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2865" data-mp="31m04ytyegsu7">
                    <input type="checkbox" checked={campaign.targetMailingLists.includes(list.id)} onChange={() => handleListToggle(list.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-2866" data-mp="2hw7vqr3dm7rv" />
                    <div className="flex-1 min-w-0" data-id="element-2867" data-mp="1hnrqk6msvxl9">
                      <div className="text-sm font-medium text-gray-900 truncate" data-id="element-2868" data-mp="1hnhuq2f2qw7n">
                        {list.Name}
                      </div>
                      {list.Subscriber_Count !== undefined && <div className="text-xs text-gray-500" data-id="element-2869" data-mp="18tlef48lce05">
                          {list.Subscriber_Count} subscribers
                        </div>}
                    </div>
                  </label>)}
              </div>
            </div>

            {/* Tags */}
            <div data-id="element-2870" data-mp="u0ryxitbjxvh">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-2871" data-mp="pwk5x9ftvvrg">
                <Tag className="h-4 w-4 text-gray-400" data-id="element-2872" data-mp="ky0jl7xbohyk" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-2873" data-mp="2kal3zs3k0eqh">
                {tags.map(tag => <button key={tag.id} type="button" onClick={() => handleTagToggle(tag.id)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${campaign.targetTags.includes(tag.id) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`} data-id="element-2874" data-mp="2fc1ghn9bauw0">
                    {campaign.targetTags.includes(tag.id) && <CheckCircle2 className="h-3 w-3" data-id="element-2875" data-mp="15fogp9xwqmm9" />}
                    {tag.Name}
                  </button>)}
              </div>
            </div>

            {/* Saved Audiences */}
            {savedAudiences.length > 0 && <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-2876" data-mp="10gtm7aqkv9mb">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2" data-id="element-2877" data-mp="10h3ghaino2xt">
                  <Target className="h-4 w-4 text-gray-400" data-id="element-2878" data-mp="2ptor5ugqe3ba" />
                  Saved Audiences
                </label>
                <p className="text-xs text-gray-500 mb-3" data-id="element-2879" data-mp="j7is8zoxgan3">
                  Include contacts matching saved audience rules. Combined with
                  above criteria using OR logic.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2880" data-mp="sx2bi6gjy6ih">
                  {savedAudiences.map(audience => <label key={audience.id} className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${selectedAudienceIds.includes(audience.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2881" data-mp="o5kl2cc5nobr">
                      <input type="checkbox" checked={selectedAudienceIds.includes(audience.id)} onChange={() => handleAudienceToggle(audience.id)} className="h-4 w-4 text-blue-600 rounded border-gray-300 mt-0.5" data-id="element-2882" data-mp="1bvobtj48vtlc" />
                      <div className="flex-1 min-w-0" data-id="element-2883" data-mp="38qt71aasb0hm">
                        <div className="text-sm font-medium text-gray-900 truncate" data-id="element-2884" data-mp="1778bzuvm465i">
                          {audience.name}
                        </div>
                        {audience.description && <div className="text-xs text-gray-500 truncate mt-0.5" data-id="element-2885" data-mp="xh6xnut2a23s">
                            {audience.description}
                          </div>}
                        <div className="flex items-center gap-2 mt-1" data-id="element-2886" data-mp="oiuxp26b1ryo">
                          <span className="text-xs text-gray-400" data-id="element-2887" data-mp="jifngjhbl0kx">
                            {getAudienceRuleSummary(audience)}
                          </span>
                          {audience.lastCount != null && <span className="inline-flex items-center gap-0.5 text-xs text-gray-500" data-id="element-2888" data-mp="ajlur4740ucn">
                              <Users className="h-3 w-3" data-id="element-2889" data-mp="28wlwz58qt622" />~{audience.lastCount}
                            </span>}
                        </div>
                      </div>
                    </label>)}
                </div>
              </div>}
          </div>

          {/* Audience Summary Bar */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2890" data-mp="ec9peabkvst">
            <div className="flex items-center justify-between" data-id="element-2891" data-mp="ggh467fgcxu">
              <div data-id="element-2892" data-mp="4enqgfzkabz0">
                <div className="text-sm font-medium text-gray-900" data-id="element-2893" data-mp="4f5rrr12zgds">
                  Audience Size
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-2894" data-mp="33xf4oqa2ujh2">
                  {isSearchingAudience && searchProgress ? searchProgress : audienceResult ? `${audienceResult.count} unique contacts` : 'Click build to calculate'}
                </div>
                {!audienceResult && <div className="text-xs text-gray-400 mt-0.5" data-id="element-2895" data-mp="s59y465nlu8h">
                    {[campaign.targetRegions.length > 0 && `${campaign.targetRegions.length} region(s)`, campaign.targetMailingLists.length > 0 && `${campaign.targetMailingLists.length} list(s)`, campaign.targetTags.length > 0 && `${campaign.targetTags.length} tag(s)`, selectedAudienceIds.length > 0 && `${selectedAudienceIds.length} audience(s)`].filter(Boolean).join(' + ') || 'No criteria selected'}
                  </div>}
              </div>
              <div className="flex items-center gap-2" data-id="element-2896" data-mp="2k7uq2bkpwut7">
                {audienceResult && audienceResult.count > 0 && <button onClick={() => setShowAudienceViewer(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2897" data-mp="2fe9pqcvnjoqu">
                    <Users className="h-4 w-4" data-id="element-2898" data-mp="283drqnpkqxn" />
                    View Contacts
                  </button>}
                <button onClick={handleBuildAudience} disabled={isSearchingAudience} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2899" data-mp="72fw1xdg7w7t">
                  {isSearchingAudience ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2900" data-mp="pxxmbw0zvtc3" /> : <Target className="h-4 w-4" data-id="element-2901" data-mp="byekfkys1e88" />}
                  {isSearchingAudience ? 'Searching...' : audienceResult ? 'Rebuild Audience' : 'Build Audience'}
                </button>
              </div>
            </div>
          </div>

          {/* Audience Viewer Modal */}
          {showAudienceViewer && audienceResult && <AudienceViewerModal contacts={audienceResult.contacts} onClose={() => setShowAudienceViewer(false)} data-id="element-2902" data-mp="3h0htc234078k" />}
        </div>}

      {/* Step 2: Content */}
      {currentStep === 2 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-8" data-id="element-2903" data-mp="337t9iyaysjr6">
          {/* AI Chat Assistant */}
          <div className={`bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 flex flex-col transition-all duration-300 ${isChatFullscreen ? 'fixed inset-4 z-50 rounded-2xl shadow-2xl' : 'rounded-xl p-6 relative'}`} data-id="element-2904" data-mp="3rd57jw56ayj">
            {isChatFullscreen && <div className="absolute inset-0 -z-10 bg-black/20 backdrop-blur-sm -m-4 rounded-none" onClick={() => setIsChatFullscreen(false)} data-id="element-2905" data-mp="3lwzt0njl1fe1" />}

            <div className={`flex items-start gap-4 mb-4 ${isChatFullscreen ? 'p-6 pb-0' : ''}`} data-id="element-2906" data-mp="2ah1ei45my15f">
              <div className="p-3 bg-purple-100 rounded-lg shadow-sm" data-id="element-2907" data-mp="pyh82vqhci1q">
                <MessageSquare className="h-6 w-6 text-purple-600" data-id="element-2908" data-mp="uzn0esg8a3aq" />
              </div>
              <div className="flex-1" data-id="element-2909" data-mp="yzfc3e0x4pkb">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-2910" data-mp="13rowkej9kxf7">
                  AI Marketing Assistant
                  <span className="text-xs font-normal px-2.5 py-0.5 bg-purple-100 text-purple-700 rounded-full border border-purple-200" data-id="element-2911" data-mp="18rl58r2wpd13">
                    GPT-Powered
                  </span>
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-2912" data-mp="1iqak0aziahd4">
                  Get help with subject lines, email copy, and campaign strategy
                </p>
              </div>
              <button onClick={() => setIsChatFullscreen(!isChatFullscreen)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors" title={isChatFullscreen ? 'Minimize' : 'Fullscreen'} data-id="element-2913" data-mp="3kh47dkfqk2vo">
                {isChatFullscreen ? <Minimize2 className="h-5 w-5" data-id="element-2914" data-mp="qpxj3nm0bjou" /> : <Maximize2 className="h-5 w-5" data-id="element-2915" data-mp="14p6vec73dtnj" />}
              </button>
            </div>

            {/* Chat Messages */}
            <div className={`bg-white rounded-xl border border-purple-100 mb-4 overflow-y-auto shadow-inner flex-1 ${isChatFullscreen ? 'mx-6 mb-6' : 'max-h-[400px]'}`} data-id="element-2916" data-mp="10on1rilq9865">
              {chatMessages.length === 0 ? <div className="h-full flex flex-col items-center justify-center p-12 text-center text-gray-400" data-id="element-2917" data-mp="1ocebqfjsp949">
                  <Sparkles className="h-10 w-10 text-purple-200 mb-4" data-id="element-2918" data-mp="1je32o71br2n3" />
                  <p className="text-sm max-w-sm" data-id="element-2919" data-mp="1xc9rkxoapsam">
                    Ask me anything about your campaign strategy, subject lines,
                    or email content...
                  </p>
                </div> : <div className="p-6 space-y-6" data-id="element-2920" data-mp="2c0tauai31rji">
                  {chatMessages.map((msg, i) => <div key={i} className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : ''}`} data-id="element-2921" data-mp="2gx9wmy1t7ttf" data-mpi={i}>
                      {msg.role === 'assistant' && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200 flex items-center justify-center shadow-sm mt-1" data-id="element-2922" data-mp="272i0lynvlf0r">
                          <Sparkles className="h-4 w-4 text-purple-600" data-id="element-2923" data-mp="1y1toxalyfqr1" />
                        </div>}
                      <div className={`flex-1 max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`} data-id="element-2924" data-mp="1o9nxc1zm7bcd">
                        <div className={`inline-block rounded-2xl px-5 py-3.5 text-[15px] shadow-sm ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-gray-50/80 border border-gray-100 text-gray-900 rounded-tl-sm'}`} data-id="element-2925" data-mp="1aao6qosjkmdk">
                          {msg.role === 'assistant' ? renderMarkdown(msg.content, {
                    onUseSubject: handleUseSubjectFromChat,
                    onUsePreheader: function (text) {
                      setCampaign(function (prev) {
                        return {
                          ...prev,
                          preheader: text
                        };
                      });
                      toast.success('Preheader text applied');
                    },
                    onCopy: function (text) {
                      navigator.clipboard.writeText(text);
                      toast.success('Copied to clipboard');
                    }
                  }) : msg.content}
                        </div>
                      </div>
                      {msg.role === 'user' && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center shadow-sm mt-1" data-id="element-2926" data-mp="38r3nkzgnhla7">
                          <Users className="h-4 w-4 text-blue-600" data-id="element-2927" data-mp="3dp5eg6kbiow8" />
                        </div>}
                    </div>)}
                  {isChatLoading && <div className="flex gap-4" data-id="element-2928" data-mp="2kz6n24inifoe">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200 flex items-center justify-center shadow-sm mt-1" data-id="element-2929" data-mp="2q041q74gcrl5">
                        <Loader2 className="h-4 w-4 text-purple-600 animate-spin" data-id="element-2930" data-mp="1xcgnraxosz7h" />
                      </div>
                      <div className="bg-gray-50/80 border border-gray-100 rounded-2xl rounded-tl-sm px-5 py-3.5 text-[15px] text-gray-500 shadow-sm flex items-center gap-2" data-id="element-2931" data-mp="229qa2hps4ur3">
                        <span className="flex gap-1" data-id="element-2932" data-mp="1dqdxyv0eh8zu">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '0ms'
                  }} data-id="element-2933" data-mp="1dp3z43lq3ws5"></span>
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '150ms'
                  }} data-id="element-2934" data-mp="q2nlileru1b4"></span>
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '300ms'
                  }} data-id="element-2935" data-mp="14qil8lr755xp"></span>
                        </span>
                      </div>
                    </div>}
                </div>}
            </div>

            {/* Chat Input */}
            <div className={`flex gap-3 ${isChatFullscreen ? 'px-6 pb-6' : ''}`} data-id="element-2936" data-mp="3qxmz9kq0q16u">
              <div className="relative flex-1" data-id="element-2937" data-mp="3lysgujvyqen9">
                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendChatMessage()} placeholder="Ask for subject line ideas, copy suggestions..." className="w-full rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-[15px] py-3 pl-4 pr-12" disabled={isChatLoading} data-id="element-2938" data-mp="3ly0qbnh8373u" />
              </div>
              <button onClick={handleSendChatMessage} disabled={!chatInput.trim() || isChatLoading} className="px-6 py-3 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors flex items-center gap-2" data-id="element-2939" data-mp="2u8nrs7x9d3fp">
                {isChatLoading ? <Loader2 className="h-5 w-5 animate-spin" data-id="element-2940" data-mp="2flozybuho70g" /> : <>
                    <span className="hidden sm:inline" data-id="element-2941" data-mp="33bjdywqjo1ma">Send</span>
                    <Send className="h-4 w-4" data-id="element-2942" data-mp="37cd1hp02reen" />
                  </>}
              </button>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="grid grid-cols-2 gap-6" data-id="element-2943" data-mp="2tim0j50et2uj">
            <div className="col-span-2" data-id="element-2944" data-mp="2yihxwlelpw14">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2945" data-mp="2yh6r0cpv085s">
                Campaign Name (Internal)
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.name} onChange={e => setCampaign({
            ...campaign,
            name: e.target.value
          })} data-id="element-2946" data-mp="3r02lvb5omcdu" />
            </div>
            <div className="col-span-2" data-id="element-2947" data-mp="90i5ra8arbv2">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2948" data-mp="3hfs0tydrrgtc">
                Subject Line
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.subject} onChange={e => setCampaign({
            ...campaign,
            subject: e.target.value
          })} data-id="element-2949" data-mp="3deyhlib9uq6s" />
            </div>
            <div className="col-span-2" data-id="element-2950" data-mp="11oe2gexv93ru">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2951" data-mp="37dg0vt86g5ci">
                Preheader Text
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.preheader} onChange={e => setCampaign({
            ...campaign,
            preheader: e.target.value
          })} data-id="element-2952" data-mp="3cd2h8bjay1pj" />
            </div>
            <div data-id="element-2953" data-mp="3v8iro6qjkivm">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2954" data-mp="3wg0lekioyzb">
                From Name
              </label>
              <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromName} onChange={e => setCampaign({
            ...campaign,
            fromName: e.target.value
          })} data-id="element-2955" data-mp="8vk880rsmonj" />
            </div>
            <div data-id="element-2956" data-mp="1xh15fn6n7a69">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2957" data-mp="1tdbif25m90nc">
                From Email
              </label>
              <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={campaign.fromEmail} onChange={e => setCampaign({
            ...campaign,
            fromEmail: e.target.value
          })} data-id="element-2958" data-mp="1oeru4v3jvcrt" />
            </div>

            <div className="col-span-2" data-id="element-2959" data-mp="27cuegwl5klja">
              <div className="border-b border-gray-200 mb-4" data-id="element-2960" data-mp="34s8bqo8u2c0i">
                <div className="flex items-center justify-between mb-2" data-id="element-2961" data-mp="34ul13mgx7onl">
                  <nav className="-mb-px flex gap-4" data-id="element-2962" data-mp="30100rnruuil8">
                    <button type="button" onClick={() => setContentTab('builder')} className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center gap-2 ${contentTab === 'builder' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2963" data-mp="3iya8de1ywrjs">
                      <LayoutTemplateIcon className="h-4 w-4" data-id="element-2964" data-mp="3e57d91hjla1h" />
                      Builder
                    </button>
                    <button type="button" onClick={() => setContentTab('html')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'html' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2965" data-mp="356lyzij3sh6i">
                      HTML Content
                    </button>
                    <button type="button" onClick={() => setContentTab('text')} className={`py-2 px-1 border-b-2 text-sm font-medium ${contentTab === 'text' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-2966" data-mp="th2wgo2d3pbe">
                      Plain Text
                    </button>
                  </nav>
                  <div className="flex items-center gap-2" data-id="element-2967" data-mp="2ixyby4zznew9">
                    {/* Preview button — always available */}
                    <button onClick={() => setShowPreview(true)} disabled={!campaign.htmlContent && contentTab !== 'text'} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2968" data-mp="2xlrw8300r5l6">
                      <Eye className="h-3.5 w-3.5" data-id="element-2969" data-mp="1ca05608crxus" />
                      Preview
                    </button>
                    {/* View Code button — only in Builder tab */}
                    {contentTab === 'builder' && <button onClick={() => setShowBuilderCode(!showBuilderCode)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border ${showBuilderCode ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-2970" data-mp="qgkqvr1n1ekd">
                        <Code className="h-3.5 w-3.5" data-id="element-2971" data-mp="3i7q1iym9wzs" />
                        {showBuilderCode ? 'Hide Code' : 'View Code'}
                      </button>}
                    {/* Copy HTML button — available in Builder and HTML tabs */}
                    {(contentTab === 'html' || contentTab === 'builder') && campaign.htmlContent && <button onClick={handleCopyHtml} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2972" data-mp="1nvgjbxolbii2">
                          {copiedHtml ? <>
                              <Check className="h-3.5 w-3.5 text-green-600" data-id="element-2973" data-mp="33mipyldgl72d" />
                              Copied
                            </> : <>
                              <Copy className="h-3.5 w-3.5" data-id="element-2974" data-mp="2unnh11j15ivd" />
                              Copy HTML
                            </>}
                        </button>}
                  </div>
                </div>
              </div>

              {/* Merge Tags Reference */}
              <div className="mb-4" data-id="element-2975" data-mp="2fqwi0td9q96a">
                <button onClick={() => setShowMergeTags(!showMergeTags)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-id="element-2976" data-mp="ix31hx1ppufw">
                  <Tag className="h-4 w-4" data-id="element-2977" data-mp="15o4f6mcs3j5q" />
                  Merge Tags Reference
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${showMergeTags ? 'rotate-180' : ''}`} data-id="element-2978" data-mp="15m1gzf1h2jpx" />
                </button>
                {showMergeTags && <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2979" data-mp="1afe39iioiefi">
                    <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-2980" data-mp="mpjsd6hx6zxw">
                      {[{
                  tag: '{{first_name}}',
                  label: 'First Name'
                }, {
                  tag: '{{last_name}}',
                  label: 'Last Name'
                }, {
                  tag: '{{email}}',
                  label: 'Email'
                }, {
                  tag: '{{team_name}}',
                  label: 'Team Name'
                }, {
                  tag: '{{unsubscribe_link}}',
                  label: 'Unsubscribe Link'
                }].map(({
                  tag,
                  label
                }) => <div key={tag} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200" data-id="element-2981" data-mp="21oz4kxjonqlz">
                          <div data-id="element-2982" data-mp="2ycvlk3j18nxq">
                            <div className="font-mono text-gray-900" data-id="element-2983" data-mp="2yd563blb62w1">{tag}</div>
                            <div className="text-gray-500" data-id="element-2984" data-mp="2km0aqloz3wri">{label}</div>
                          </div>
                          <button onClick={async () => {
                    await navigator.clipboard.writeText(tag);
                    toast.success('Copied to clipboard');
                  }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-2985" data-mp="2omk5jim2qoah">
                            <Copy className="h-3.5 w-3.5" data-id="element-2986" data-mp="3qx93z7nsjy6j" />
                          </button>
                        </div>)}
                    </div>
                  </div>}
              </div>

              {/* Content Editor */}
              {contentTab === 'builder' ? <div className="space-y-4" data-id="element-2987" data-mp="380hdoe9u0isz">
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2988" data-mp="381izndsdypph">
                    <EmailBuilder initialBlocks={emailBuilderBlocks} initialStyles={emailBuilderStyles} onSchemaChange={(blocks, styles) => {
                setEmailBuilderBlocks(blocks);
                setEmailBuilderStyles(styles);
              }} onHtmlChange={html => {
                setCampaign(prev => ({
                  ...prev,
                  htmlContent: html
                }));
              }} mergeVariables={['first_name', 'last_name', 'email', 'team_name', 'unsubscribe_link']} data-id="element-2989" data-mp="339itv4pmx2kz" />
                  </div>
                  {/* Generated HTML Code Panel */}
                  {showBuilderCode && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2990" data-mp="1wfl2hfqhr57k">
                      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200" data-id="element-2991" data-mp="1wd8o1cjxzcz9">
                        <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5" data-id="element-2992" data-mp="2654nos1ofb5l">
                          <Code className="h-3.5 w-3.5" data-id="element-2993" data-mp="18q8zixlrbtdp" />
                          Generated HTML
                          {campaign.htmlContent ? ` (${campaign.htmlContent.length.toLocaleString()} chars)` : ''}
                        </span>
                        {campaign.htmlContent && <button onClick={handleCopyHtml} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded" data-id="element-2994" data-mp="3agznp9auo69p">
                            {copiedHtml ? <Check className="h-3 w-3 text-green-600" data-id="element-2995" data-mp="217kor9cryno" /> : <Copy className="h-3 w-3" data-id="element-2996" data-mp="bxiw7f14ckh2" />}
                            {copiedHtml ? 'Copied' : 'Copy'}
                          </button>}
                      </div>
                      {campaign.htmlContent ? <pre className="p-4 text-xs font-mono bg-gray-900 text-gray-300 overflow-auto max-h-[400px] whitespace-pre-wrap break-all" data-id="element-2997" data-mp="2dqqnznxx4uju">
                          {campaign.htmlContent}
                        </pre> : <div className="p-6 text-center text-sm text-gray-400 bg-gray-50" data-id="element-2998" data-mp="t5tn52vtj8m7">
                          <Code className="h-8 w-8 mx-auto mb-2 text-gray-300" data-id="element-2999" data-mp="eiqm4m8aehu4" />
                          <p data-id="element-3000" data-mp="jgjtvjp8njwi">No HTML generated yet.</p>
                          <p className="text-xs mt-1" data-id="element-3001" data-mp="jf8tltv04ua5">
                            Add blocks in the builder above to see the generated
                            code.
                          </p>
                        </div>}
                    </div>}
                </div> : contentTab === 'html' ? <CodeEditor label="Email HTML" language="html" value={campaign.htmlContent} onChange={e => setCampaign({
            ...campaign,
            htmlContent: e.target.value
          })} className="min-h-[300px]" data-id="element-3002" data-mp="hecstnmteh7" /> : <textarea rows={12} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono" value={campaign.textContent} onChange={e => setCampaign({
            ...campaign,
            textContent: e.target.value
          })} placeholder="Enter plain text version..." data-id="element-3003" data-mp="1oedr1vv305gy" />}
            </div>
          </div>
        </div>}

      {/* Campaign Preview Modal */}
      {showPreview && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-3004" data-mp="2uvsj8dqiw5wo">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPreview(false)} data-id="element-3005" data-mp="2uy57f8mvjci6" />
          <div className="absolute inset-4 sm:inset-8 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-3006" data-mp="cn7r8y0ldifh">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-3007" data-mp="hm120iohuvby">
              <div className="flex items-center gap-4" data-id="element-3008" data-mp="lppar4ov3wys">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-3009" data-mp="qniii25tcz16">
                  Campaign Preview
                </h3>
                {campaign.subject && <span className="text-sm text-gray-500" data-id="element-3010" data-mp="10kddwc6sijb8">
                    Subject:{' '}
                    <span className="font-medium text-gray-700" data-id="element-3011" data-mp="15blmj60aq0nc">
                      {campaign.subject}
                    </span>
                  </span>}
              </div>
              <div className="flex items-center gap-3" data-id="element-3012" data-mp="1ec1q0fh1h7s7">
                <button onClick={handleCopyHtml} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3013" data-mp="1edl4q1n1rarv">
                  {copiedHtml ? <>
                      <Check className="h-3.5 w-3.5 text-green-600" data-id="element-3014" data-mp="paz03tcxwj1o" /> Copied
                    </> : <>
                      <Copy className="h-3.5 w-3.5" data-id="element-3015" data-mp="z6irc2gk6tk3" /> Copy HTML
                    </>}
                </button>
                <button onClick={() => setShowPreview(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" data-id="element-3016" data-mp="bi9aley7cvzt">
                  <X className="h-5 w-5" data-id="element-3017" data-mp="kal1t3154ngu" />
                </button>
              </div>
            </div>
            {/* Preview Content */}
            <div className="flex-1 overflow-auto bg-gray-100 p-4" data-id="element-3018" data-mp="fh8dyv4ackp5">
              {campaign.htmlContent ? <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-3019" data-mp="3jx9j6phu9bx5">
                  <iframe srcDoc={campaign.htmlContent} title="Campaign Preview" className="w-full border-0" style={{
              minHeight: '600px',
              height: '100%'
            }} sandbox="allow-same-origin" data-id="element-3020" data-mp="39tcp5nx4r3cn" />
                </div> : campaign.textContent ? <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm p-8" data-id="element-3021" data-mp="3skvq7p7lg7c1">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed" data-id="element-3022" data-mp="1e2moepbvw4c">
                    {campaign.textContent}
                  </pre>
                </div> : <div className="flex items-center justify-center h-full text-gray-400" data-id="element-3023" data-mp="3njpwbo276e0x">
                  <div className="text-center" data-id="element-3024" data-mp="27682s2jqaizb">
                    <Eye className="h-12 w-12 mx-auto mb-3 text-gray-300" data-id="element-3025" data-mp="278lvw5uull48" />
                    <p className="text-sm" data-id="element-3026" data-mp="2c6dqx8qshncn">No content to preview yet.</p>
                    <p className="text-xs mt-1" data-id="element-3027" data-mp="3jb6ko18y3iw4">
                      Add HTML content or use the drag-and-drop builder.
                    </p>
                  </div>
                </div>}
            </div>
          </div>
        </div>}

      {/* Step 3: Review */}
      {currentStep === 3 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3028" data-mp="3fanqi0jky0qr">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-3029" data-mp="6v44nml8nklq">Review Campaign</h2>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3030" data-mp="avwbk96n1deu">
            <div className="grid grid-cols-2 gap-6" data-id="element-3031" data-mp="axh5pxp1iuc6">
              <div className="col-span-2" data-id="element-3032" data-mp="3t8ugyvm78yge">
                <span className="text-sm text-gray-500 block" data-id="element-3033" data-mp="3t7t36jdstxuz">
                  Campaign Name
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3034" data-mp="pt6uu4pivnl4">
                  {campaign.name || <span className="text-gray-400 italic" data-id="element-3035" data-mp="uk5hdm13thwt">Not set</span>}
                </span>
              </div>
              <div data-id="element-3036" data-mp="13ww4g2y1l2kh">
                <span className="text-sm text-gray-500 block" data-id="element-3037" data-mp="13wcm4l4hcc5x">Subject</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3038" data-mp="zw2d0zle55mu">
                  {campaign.subject}
                </span>
              </div>
              <div data-id="element-3039" data-mp="18p5t7fnf7wl6">
                <span className="text-sm text-gray-500 block" data-id="element-3040" data-mp="18ovyjerfl195">From</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3041" data-mp="kzl7iozvul87">
                  {campaign.fromName} &lt;{campaign.fromEmail}&gt;
                </span>
              </div>
              <div data-id="element-3042" data-mp="h02mlt82aa5d">
                <span className="text-sm text-gray-500 block" data-id="element-3043" data-mp="uxr7v261jjay">
                  Audience Size
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-3044" data-mp="py4rwkytvoyg">
                  {audienceResult ? `${audienceResult.count} contacts` : 'Not calculated'}
                </span>
              </div>
              <div data-id="element-3045" data-mp="21db9nq7fyzf7">
                <span className="text-sm text-gray-500 block" data-id="element-3046" data-mp="21fxlw35a233r">Targeting</span>
                <div className="flex flex-wrap gap-1 mt-1" data-id="element-3047" data-mp="29dzjgbu833ka">
                  {campaign.targetRegions.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3048" data-mp="2dhyu4dstn85q">
                      {campaign.targetRegions.length} Regions
                    </span>}
                  {campaign.targetMailingLists.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-3049" data-mp="1pop2kuiqqckl">
                      {campaign.targetMailingLists.length} Lists
                    </span>}
                  {campaign.targetTags.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-3050" data-mp="24h945qq8sbzx">
                      {campaign.targetTags.length} Tags
                    </span>}
                  {selectedAudienceIds.length > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-3051" data-mp="14djblr66zmde">
                      {selectedAudienceIds.length} Saved Audience
                      {selectedAudienceIds.length !== 1 ? 's' : ''}
                    </span>}
                </div>
                {selectedAudienceIds.length > 0 && <div className="mt-2 space-y-1" data-id="element-3052" data-mp="li2trawmi2xa">
                    {selectedAudienceIds.map(id => {
                const aud = savedAudiences.find(a => a.id === id);
                if (!aud) return null;
                return <div key={id} className="text-xs text-gray-500 flex items-center gap-1" data-id="element-3053" data-mp="6sn4y0gnk7k8">
                          <Target className="h-3 w-3 text-amber-500" data-id="element-3054" data-mp="3twtizgppz9xw" />
                          {aud.name} — {getAudienceRuleSummary(aud)}
                        </div>;
              })}
                  </div>}
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4" data-id="element-3055" data-mp="dq1d21yd67m9">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-3056" data-mp="dnf35vo03g0v">
              Compliance Checklist
            </h3>
            <div className="space-y-2" data-id="element-3057" data-mp="3rlkdujouqan">
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-3058" data-mp="3qk9bq2oa3sx">
                <CheckCircle2 className="h-4 w-4" data-id="element-3059" data-mp="8qoqliben2k7" />
                Sending to confirmed subscribers only
              </div>
              <div className="flex items-center gap-2 text-sm text-green-700" data-id="element-3060" data-mp="3qxcrevzf93k2">
                <CheckCircle2 className="h-4 w-4" data-id="element-3061" data-mp="3uyobklzj0wkv" />
                Unsubscribe link included automatically
              </div>
            </div>
          </div>
        </div>}

      {/* Step 4: Export */}
      {currentStep === 4 && !exportResult && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3062" data-mp="1t3xefhilzvv3">
          <div className="text-center py-4" data-id="element-3063" data-mp="3dly6fj7bp8pi">
            <div className="mx-auto h-16 w-16 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-3064" data-mp="39nqr8ssicfgf">
              <Database className="h-8 w-8 text-purple-600" data-id="element-3065" data-mp="3nevvk86tlvx3" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3066" data-mp="3in5frmp46fya">
              Push to Zoho CRM
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-3067" data-mp="15kv47fw55ph">
              This will update <strong data-id="element-3068" data-mp="3sdgtu22ii5iw">{audienceResult?.count || 0}</strong>{' '}
              contacts in Zoho CRM with a Bucket ID.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-id="element-3069" data-mp="a72a1mhkb1cl">
            <div className="flex items-center justify-between" data-id="element-3070" data-mp="59sk85q9gno8">
              <span className="text-sm text-blue-700" data-id="element-3071" data-mp="2byfanzchyn2u">Bucket ID:</span>
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-blue-200 text-blue-900" data-id="element-3072" data-mp="2c0s2d4824bt3">
                {bucketConfig.bucketId}
              </code>
            </div>
            <div className="flex items-center justify-between" data-id="element-3073" data-mp="ayu063x1oiml">
              <span className="text-sm text-blue-700" data-id="element-3074" data-mp="fwlnslqnpikz">Contacts:</span>
              <span className="text-sm font-medium text-blue-900" data-id="element-3075" data-mp="kudpu8l0ml2w">
                {audienceResult?.count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3076" data-mp="oxto19cqgthp">
              <span className="text-sm text-blue-700 flex items-center gap-1" data-id="element-3077" data-mp="tuk1dox5lazf">
                Batches needed:
                <span className="relative group" data-id="element-3078" data-mp="ytdehg8j2zz2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-400 cursor-help" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3079" data-mp="13lelonqua0i8">
                    <circle cx="12" cy="12" r="10" data-id="element-3080" data-mp="322ymzlk3ouqj" />
                    <path d="M12 16v-4" data-id="element-3081" data-mp="324rt8zqsxcy0" />
                    <path d="M12 8h.01" data-id="element-3082" data-mp="2x43wryjt03we" />
                  </svg>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 leading-relaxed" data-id="element-3083" data-mp="2x2srtvemgq3p">
                    Zoho CRM API processes contacts in batches of 100. This
                    shows how many API calls are needed to update all contacts
                    with the Bucket ID.
                  </span>
                </span>
              </span>
              <span className="text-sm font-medium text-blue-900" data-id="element-3084" data-mp="3bzjss93y29vf">
                {Math.ceil((audienceResult?.count || 0) / 100)}
              </span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-3085" data-mp="8d2yxyuqev4h">
            <label className="block text-sm font-medium text-gray-700" data-id="element-3086" data-mp="8e4dweevc1rh">
              Bucket Expiry (Optional)
            </label>
            <div className="grid grid-cols-4 gap-3" data-id="element-3087" data-mp="382w11pu0ja5j">
              {[null, 24, 48, 72].map(hours => <button key={hours || 'none'} onClick={() => setBucketConfig(prev => ({
            ...prev,
            expiryHours: hours
          }))} disabled={isExporting} className={`px-4 py-2 text-sm font-medium rounded-md border ${bucketConfig.expiryHours === hours ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} disabled:opacity-50`} data-id="element-3088" data-mp="3cudwxzh6ikjn">
                  {hours ? `${hours} hours` : 'No expiry'}
                </button>)}
            </div>
          </div>

          {/* Progress Display */}
          {isExporting && exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-id="element-3089" data-mp="2afdnm1pepjv7">
              <div className="flex items-center justify-between text-sm" data-id="element-3090" data-mp="22vuqkrjbvq3m">
                <span className="text-purple-700 font-medium" data-id="element-3091" data-mp="22xxp5zyjqrjy">
                  Updating contacts...
                </span>
                <span className="text-purple-900 font-mono text-xs" data-id="element-3092" data-mp="2cu0oyz5xee3q">
                  Batch {exportProgress.currentBatch} /{' '}
                  {exportProgress.totalBatches}
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2.5" data-id="element-3093" data-mp="1k4tv4dhf2cot">
                <div className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" style={{
            width: `${Math.round(exportProgress.processedContacts / exportProgress.totalContacts * 100)}%`
          }} data-id="element-3094" data-mp="1f6s9plwiq1a6" />
              </div>
              <div className="flex justify-between text-xs text-purple-600" data-id="element-3095" data-mp="11elr7t5w7i7k">
                <span data-id="element-3096" data-mp="wg25ntv7o8fq">
                  {exportProgress.processedContacts} /{' '}
                  {exportProgress.totalContacts} contacts processed
                </span>
                <span data-id="element-3097" data-mp="3s40nqeansaqm">
                  {exportProgress.updatedCount} updated
                  {exportProgress.failedCount > 0 ? `, ${exportProgress.failedCount} failed` : ''}
                </span>
              </div>
            </div>}

          {isExporting && !exportProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center" data-id="element-3098" data-mp="evxlkvm8wld3">
              <Loader2 className="h-5 w-5 animate-spin text-purple-600 mx-auto" data-id="element-3099" data-mp="9wkpdk9hi1xu" />
              <p className="text-sm text-purple-700 mt-2" data-id="element-3100" data-mp="4x81qxasgqu7">
                Preparing export...
              </p>
            </div>}

          <div className="flex justify-center gap-4 pt-4" data-id="element-3101" data-mp="3w2hli786w8vd">
            <button onClick={() => setCurrentStep(3)} disabled={isExporting} className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-3102" data-mp="2pnfm6xk1o37h">
              Back
            </button>
            <button onClick={handleExport} disabled={isExporting || !audienceResult?.count} className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:opacity-50" data-id="element-3103" data-mp="155ejo1xpf7yt">
              {isExporting ? <>
                  <Loader2 className="h-5 w-5 animate-spin" data-id="element-3104" data-mp="1jtj1z4suhhh2" />
                  Pushing to CRM...
                </> : <>
                  <ExternalLink className="h-5 w-5" data-id="element-3105" data-mp="1suzb797xbdjp" />
                  Push to Zoho CRM
                </>}
            </button>
          </div>
        </div>}

      {/* Export Success */}
      {currentStep === 4 && exportResult?.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3106" data-mp="3uo6pqgfppr4j">
          <div className="text-center" data-id="element-3107" data-mp="3gkpyyw468sy">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-3108" data-mp="3rkhfisvd6m51">
              <CheckCircle2 className="h-8 w-8 text-green-600" data-id="element-3109" data-mp="3njnr7ye0f5bm" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3110" data-mp="5dj57skcjclk">
              Export Successful!
            </h2>
            <p className="mt-2 text-gray-600" data-id="element-3111" data-mp="dwrzj4ipwcr">
              <strong data-id="element-3112" data-mp="d4xycqkk6ol">{exportResult.updatedCount}</strong> contacts updated in
              Zoho CRM.
            </p>
          </div>

          {exportResult.failedCount > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-3113" data-mp="abuhe9y45z6y">
              <div className="flex items-start gap-2" data-id="element-3114" data-mp="o2z82mji7h55">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-3115" data-mp="o5v60avcq5tl" />
                <div data-id="element-3116" data-mp="j70mt7th2f8y">
                  <p className="text-sm font-medium text-yellow-900" data-id="element-3117" data-mp="y1yioantxmus">
                    {exportResult.failedCount} contact(s) failed to update
                  </p>
                  {exportResult.errors?.length > 0 && <ul className="mt-1 text-xs text-yellow-700 space-y-1" data-id="element-3118" data-mp="1condw0b1tdvd">
                      {exportResult.errors.slice(0, 5).map((e: string, i: number) => <li key={i} data-id="element-3119" data-mp="12pxxkbysw1h8" data-mpi={i}>• {e}</li>)}
                      {exportResult.errors.length > 5 && <li data-id="element-3120" data-mp="17rnaxy9hwr8h">...and {exportResult.errors.length - 5} more</li>}
                    </ul>}
                </div>
              </div>
            </div>}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-3121" data-mp="k085hwmf9tt0">
            <h3 className="text-base font-semibold text-blue-900 mb-4" data-id="element-3122" data-mp="jy54yinn8ia1">
              Next Steps in Zoho Campaigns
            </h3>
            <ol className="space-y-4 text-sm text-blue-900" data-id="element-3123" data-mp="a2bx3ckvdcyl">
              <li className="flex gap-3" data-id="element-3124" data-mp="a2twgi2tw7aq">
                <span className="font-semibold" data-id="element-3125" data-mp="f1ogtogg23wy">1.</span>
                <span data-id="element-3126" data-mp="f39azcyujkua">Go to Zoho Campaigns and create a Segment</span>
              </li>
              <li className="flex gap-3" data-id="element-3127" data-mp="1ib9blt7sxy7m">
                <span className="font-semibold" data-id="element-3128" data-mp="1dd7uvexumazb">2.</span>
                <div className="flex-1" data-id="element-3129" data-mp="1ddrbmsbritbr">
                  <p className="mb-2" data-id="element-3130" data-mp="18lzlgiwiqpm5">Use this filter criteria:</p>
                  <div className="bg-white border border-blue-300 rounded p-3 font-mono text-sm flex justify-between items-center" data-id="element-3131" data-mp="18k6batmp5n9e">
                    <code data-id="element-3132" data-mp="yntli6u9vtjx">Bucket_ID equals {bucketConfig.bucketId}</code>
                    <button onClick={() => handleCopyToClipboard(`Bucket_ID equals ${bucketConfig.bucketId}`, 'bucket-filter')} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200" data-id="element-3133" data-mp="ylqj0nbxo7yb">
                      {copiedItem === 'bucket-filter' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-3134" data-mp="2mlb3srumm325">
                <span className="font-semibold" data-id="element-3135" data-mp="2rkxeoumzf77s">3.</span>
                <span data-id="element-3136" data-mp="2rjckj64kxqag">
                  Send your campaign, then clear the bucket from Send Buckets
                  page
                </span>
              </li>
            </ol>
          </div>

          <div className="text-center pt-4" data-id="element-3137" data-mp="31a78cexh4xy0">
            <button onClick={() => {
          clearCampaignDraft();
          navigate('/campaigns');
        }} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3138" data-mp="23mzxm8yxaenc">
              Return to Campaigns
            </button>
          </div>
        </div>}

      {/* Export Failed */}
      {currentStep === 4 && exportResult && !exportResult.success && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 space-y-6" data-id="element-3139" data-mp="3ozt4sumgoz2b">
          <div className="text-center" data-id="element-3140" data-mp="3txuo9gnsv0eb">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center" data-id="element-3141" data-mp="1tx67nfbg0arn">
              <X className="h-8 w-8 text-red-600" data-id="element-3142" data-mp="1oybscncop4ca" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-gray-900" data-id="element-3143" data-mp="1p06358u8rg2n">
              Export Failed
            </h2>
            <p className="mt-2 text-gray-500" data-id="element-3144" data-mp="22vi5o2en2au4">{exportResult.error}</p>
          </div>
          <div className="text-center" data-id="element-3145" data-mp="1yu507tfgk9nq">
            <button onClick={() => {
          setExportResult(null);
          setExportProgress(null);
        }} className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3146" data-mp="1b65dd4l9p3ef">
              Try Again
            </button>
          </div>
        </div>}

      {/* Navigation Buttons (Steps 1-3) */}
      {currentStep < 4 && <div className="flex justify-between pt-6 border-t border-gray-200" data-id="element-3147" data-mp="rhd2vcq5w70d">
          <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50" data-id="element-3148" data-mp="nhvvuev371t3">
            Back
          </button>
          <button onClick={() => {
        if (currentStep === 1) {
          const hasCrmCriteria = campaign.targetRegions.length > 0 || campaign.targetMailingLists.length > 0 || campaign.targetTags.length > 0;
          const hasAudienceCriteria = selectedAudienceIds.length > 0;
          // If no criteria at all, block
          if (!hasCrmCriteria && !hasAudienceCriteria) {
            toast.error('Please select at least one criteria or saved audience');
            return;
          }
          // If CRM criteria are set but audience hasn't been built, require build
          if (hasCrmCriteria && !audienceResult) {
            toast.error('Please click "Build Audience" to calculate your audience size');
            return;
          }
          // If audience was built but is empty
          if (audienceResult && audienceResult.count === 0) {
            toast.error('Audience is empty — adjust your criteria');
            return;
          }
          // If only saved audiences selected (no CRM criteria), allow proceeding
          // The saved audience rules will be applied during send
        }
        setCurrentStep(Math.min(4, currentStep + 1));
      }} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3149" data-mp="3f7z12nbj2na">
            Next Step
            <ArrowRight className="h-4 w-4" data-id="element-3150" data-mp="3um17urmgv7tm" />
          </button>
        </div>}
    </div>;
}